import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { Role } from "./roles";
import { DEMO_MODE } from "./demo-data";

const STORAGE_KEY = "nl-notices-v1";

export type NoticeKind = "editor_note" | "changes" | "submitted" | "approved" | "campaign";

export interface Notice {
  id: string;
  kind: NoticeKind;
  title: string;
  body: string;
  href?: string;
  at: string;
  read: boolean;
  forRoles: Role[];
}

function newId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `n-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function seedNotices(): Notice[] {
  return [
    {
      id: "n-seed-1",
      kind: "editor_note",
      title: "Note from Dr. Sami",
      body: "Subject line on “Women's health focus” needs medical review before approval.",
      href: "/content",
      at: "2026-08-11T09:20:00Z",
      read: false,
      forRoles: ["freelancer", "editor", "admin"],
    },
    {
      id: "n-seed-2",
      kind: "submitted",
      title: "Ready for review",
      body: "Nour submitted “Women's health focus”. Open the board to approve or request changes.",
      href: "/content",
      at: "2026-08-11T09:05:00Z",
      read: false,
      forRoles: ["editor", "admin"],
    },
    {
      id: "n-seed-3",
      kind: "campaign",
      title: "Campaign scheduled",
      body: "Summer skincare send is set for 14 Aug to Weekly digest — opted in.",
      href: "/broadcasts",
      at: "2026-08-10T12:00:00Z",
      read: false,
      forRoles: ["admin", "editor"],
    },
    {
      id: "n-seed-4",
      kind: "approved",
      title: "Newsletter approved",
      body: "“صيف 2026 — نصائح الترطيب” is approved. You can put it in a campaign.",
      href: "/broadcasts",
      at: "2026-08-10T11:40:00Z",
      read: true,
      forRoles: ["freelancer", "editor", "admin"],
    },
  ];
}

function readStored(): Notice[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    // The four sample notices were seeded into every browser on the live site.
    return DEMO_MODE ? parsed as Notice[] : (parsed as Notice[]).filter((n) => !String(n.id).startsWith("n-seed-"));
  } catch {
    return null;
  }
}

function writeStored(notices: Notice[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(notices));
}

type Ctx = {
  notices: Notice[];
  push: (input: Omit<Notice, "id" | "at" | "read"> & { at?: string; read?: boolean }) => void;
  markRead: (id: string) => void;
  markAllRead: (role: Role) => void;
};

const NotifyContext = createContext<Ctx | null>(null);

export function NotifyProvider({ children }: { children: ReactNode }) {
  const [notices, setNotices] = useState<Notice[]>(() => {
    const stored = readStored();
    if (stored) return stored;
    const seed = DEMO_MODE ? seedNotices() : [];
    writeStored(seed);
    return seed;
  });

  const persist = useCallback((updater: (cur: Notice[]) => Notice[]) => {
    setNotices((cur) => {
      const next = updater(cur);
      writeStored(next);
      return next;
    });
  }, []);

  const push: Ctx["push"] = useCallback((input) => {
    const next: Notice = {
      id: newId(),
      at: input.at ?? new Date().toISOString(),
      read: input.read ?? false,
      kind: input.kind,
      title: input.title,
      body: input.body,
      href: input.href,
      forRoles: input.forRoles,
    };
    persist((cur) => [next, ...cur].slice(0, 40));
  }, [persist]);

  const markRead = useCallback((id: string) => {
    persist((cur) => cur.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, [persist]);

  const markAllRead = useCallback((role: Role) => {
    persist((cur) => cur.map((n) => (n.forRoles.includes(role) ? { ...n, read: true } : n)));
  }, [persist]);

  const value = useMemo(() => ({ notices, push, markRead, markAllRead }), [notices, push, markRead, markAllRead]);

  return <NotifyContext.Provider value={value}>{children}</NotifyContext.Provider>;
}

export function useNotify() {
  const ctx = useContext(NotifyContext);
  if (!ctx) throw new Error("useNotify must be used inside NotifyProvider");
  return ctx;
}

export function noticesForRole(notices: Notice[], role: Role): Notice[] {
  return notices.filter((n) => n.forRoles.includes(role));
}
